import { css } from '@emotion/react';
import { Clear } from '@mui/icons-material';
import { IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { BasicDragHandle } from '../../../../../components/SortableList/SortableItem';
import { colors } from '../../../../../theme/colors';
import { SectionTypeLabel, TemplateSectionFormValues } from '../../../../../schemas/kpi-template.schema';
import { sectionIconSx, SectionTypeIcon, toggleGroupSx } from './section-editor.styles';

const headerCss = css`
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid ${colors.neutral[20]};
  background: ${colors.neutral[5]};
`;

interface SectionHeaderProps {
  section: Pick<TemplateSectionFormValues, 'type' | 'sectionType'>;
  onDelete: () => void;
  onToggle: (value: KpiSection) => void;
  toggleDisabled: boolean;
}

export function SectionHeader({ section, onDelete, onToggle, toggleDisabled }: SectionHeaderProps) {
  const Icon = SectionTypeIcon[section.type];

  const handleToggleChange = (_: React.MouseEvent<HTMLElement>, newValue: KpiSection | null) => {
    if (newValue === null) {
      return;
    }
    onToggle(newValue);
  };

  return (
    <Stack css={headerCss} direction='row' alignItems='center' gap='0.5rem'>
      <BasicDragHandle />
      <Icon htmlColor={colors.secondary[40]} aria-hidden='true' sx={sectionIconSx} />
      <Typography variant='subtitle2' sx={{ flex: 1 }}>
        {SectionTypeLabel[section.type]}
      </Typography>
      <ToggleButtonGroup
        value={section.sectionType}
        exclusive
        size='small'
        color='secondary'
        onChange={handleToggleChange}
        disabled={toggleDisabled}
        sx={toggleGroupSx}
        aria-label='Data scenario'
      >
        <ToggleButton value={KpiSection.actual}>Actual</ToggleButton>
        <ToggleButton value={KpiSection.budget}>Budget</ToggleButton>
      </ToggleButtonGroup>
      <IconButton size='small' onClick={onDelete} aria-label='Delete section'>
        <Clear fontSize='small' />
      </IconButton>
    </Stack>
  );
}
